import React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const duration = (seconds) => {
  const s = Math.max(0, Math.round(seconds || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const usersLink = (from, to, kind) => {
  if (!from || !to) return null;
  const q = new URLSearchParams(kind === "active" ? { active_from: from, active_to: to } : { from, to });
  return `/admin/mobile-users?${q.toString()}`;
};

export default function UserActivityCard({ activity }) {
  const navigate = useNavigate();
  const data = activity || {};

  // Active Users / New Users open the matching Mobile Users list (29 Oct meeting: "click 13").
  const tiles = [
    { label: "Active Users", stat: data.active_users, href: usersLink(data.from, data.to, "active") },
    { label: "New Users", stat: data.new_users, href: usersLink(data.from, data.to, "new") },
    { label: "Sessions", stat: data.sessions },
    { label: "Avg. Session", stat: data.avg_session_seconds, format: duration },
  ];

  const chartData = {
    labels: data.labels || [],
    datasets: [{
      data: data.active_by_day || [],
      borderColor: "#7c3aed",
      backgroundColor: "rgba(124,58,237,0.12)",
      fill: true,
      tension: 0.4,
      pointRadius: 3,
    }],
  };

  return (
    <Box>
      <Grid container spacing={1}>
        {tiles.map((t) => {
          const value = t.stat?.value;
          const delta = Number(t.stat?.delta_pct ?? 0);
          const up = delta >= 0;
          return (
            <Grid item xs={6} sm={3} key={t.label}>
              <Box
                onClick={t.href ? () => navigate(t.href) : undefined}
                role={t.href ? "link" : undefined}
                title={t.href ? `See these users` : undefined}
                sx={{
                  p: 1,
                  border: "1px solid #eef0f4",
                  borderRadius: 2,
                  height: "100%",
                  ...(t.href ? { cursor: "pointer", "&:hover": { bgcolor: "#f9fafb", borderColor: "#ddd6fe" } } : {}),
                }}
              >
                <Typography variant="caption" display="block" sx={{ color: "#6b7280", fontWeight: 700 }} noWrap>
                  {t.label}
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: "#111827", lineHeight: 1.2 }}>
                  {value === undefined ? "—" : (t.format ? t.format(value) : value.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }))}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  {up ? (
                    <TrendingUpIcon sx={{ fontSize: 12, color: "#16a34a" }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 12, color: "#dc2626" }} />
                  )}
                  <Typography variant="caption" sx={{ color: up ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                    {Math.abs(delta)}%
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ height: 170, width: "100%", mt: 1.5 }}>
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.y} active users` } },
            },
            scales: {
              y: { beginAtZero: true, grid: { color: "#f1f5f9" }, ticks: { color: "#374151", precision: 0 } },
              x: { grid: { display: false }, ticks: { color: "#374151", maxRotation: 0 } },
            },
          }}
        />
      </Box>
    </Box>
  );
}
